import * as React from 'react';
import { useState, useEffect } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { SignOut24Regular } from '@fluentui/react-icons';
import { collection, getDocs } from 'firebase/firestore';
import { db, signOutAdmin } from '../firebase';
import * as XLSX from 'xlsx';
import themeImage from '../assets/theme.jpg';
import { FONT_FAMILY } from './sharedStyles';
import { DASHBOARD_CONSTANTS } from '../constants/dashboardConstants';

interface DashboardProps {
  setActiveSection: (section: 'home' | 'schedule' | 'faq' | 'rsvp' | 'admin') => void;
}

const useStyles = makeStyles({
  dashboard: {
    padding: '2rem',
    fontFamily: FONT_FAMILY,
    background: `url(${themeImage}) center/cover no-repeat fixed, linear-gradient(to bottom right, #fff8e1, #ffe0b2)`,
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
  },
  loadingCard: {
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '3rem',
    borderRadius: '15px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: '500px',
    margin: '0 auto',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '1rem',
    width: '100%',
  },
  logoutButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: 'var(--primary-color)',
    border: '2px solid var(--primary-color)',
    borderRadius: '25px',
    padding: '0.6rem 1.2rem',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(12, 59, 94, 0.15)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    selectors: {
      '&:hover': {
        backgroundColor: 'var(--primary-color)',
        color: '#ffffff',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(12, 59, 94, 0.25)',
      },
    },
  },
  title: {
    textAlign: 'center',
    marginBottom: '2rem',
    marginTop: '35px',
  },
  titleText: {
    color: 'var(--primary-color)',
    fontSize: '2.5rem',
    lineHeight: '1',
    margin: 0,
  },
  summarySection: {
    textAlign: 'center',
    marginBottom: '2rem',
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '1.5rem',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    margin: '0 auto 2rem auto',
  },
  summaryTitle: {
    color: 'var(--primary-color)',
    margin: '0 0 1rem 0',
    fontSize: '1.4rem',
    fontWeight: 600,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    fontSize: '1.1rem',
  },
  statsValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  tableContainer: {
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '2rem',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    margin: '1rem auto',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  searchContainer: {
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #e0e0e0',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    backgroundColor: '#ffffff',
    transition: 'all 0.3s ease',
    fontFamily: FONT_FAMILY,
    boxSizing: 'border-box',
    selectors: {
      '&:focus': {
        border: '2px solid var(--primary-color)',
        boxShadow: '0 0 0 3px rgba(12, 59, 94, 0.1)',
        outline: 'none',
      },
    },
  },
  clearButton: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: 'var(--primary-color)',
    cursor: 'pointer',
    fontSize: '1.2rem',
    padding: '0.25rem',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    selectors: {
      '&:hover': {
        backgroundColor: 'rgba(12, 59, 94, 0.1)',
      },
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
    minWidth: '1000px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  tableHeader: {
    backgroundColor: 'var(--primary-color)',
    color: '#fff8e1',
    fontWeight: 600,
    border: '1px solid #ddd',
    padding: '8px',
    textAlign: 'left',
  },
  tableCell: {
    border: '1px solid #ddd',
    padding: '8px',
  },
  nameCell: {
    fontWeight: 500,
    color: 'var(--primary-color)',
  },
  exportButton: {
    backgroundColor: 'var(--primary-color)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    selectors: {
      '&:hover': {
        backgroundColor: 'var(--primary-hover)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
      },
      '&:disabled': {
        backgroundColor: '#d0d0d0',
        color: '#666666',
        cursor: 'not-allowed',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  exportSection: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  noRsvpsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1',
    marginTop: '10rem',
  },
  noRsvpsCard: {
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '3rem',
    borderRadius: '15px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: '500px',
    margin: '0 auto',
  },
  emoji: {
    fontSize: '4rem',
    marginBottom: '1rem',
    lineHeight: '1',
  },
  noRsvpsTitle: {
    color: 'var(--primary-color)',
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 600,
  },
  noRsvpsText: {
    color: '#6d4c41',
    fontSize: '1.2rem',
    lineHeight: '1.6',
    marginBottom: '1.5rem',
  },
  emojiRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    fontSize: '1.5rem',
    opacity: 0.7,
  },
});

const Dashboard: React.FC<DashboardProps> = ({ setActiveSection }) => {
  const classes = useStyles();
  const [rsvps, setRsvps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const handleLogout = async () => {
    try {
      await signOutAdmin();
      setActiveSection('home');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const fetchRSVPs = async () => {
      try {
        const snapshot = await getDocs(collection(db, process.env.REACT_APP_DATABASE_NAME || 'rsvps-dev'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRsvps(data);
      } catch (err) {
        console.error('Error fetching RSVPs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRSVPs();
  });

  const exportToExcel = () => {
    const excelData = rsvps.map(rsvp => ({
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.NAME]: rsvp.name || '',
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.EVENTS]: Array.isArray(rsvp.events) ? rsvp.events.join(', ') : (rsvp.events || ''),
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.GUESTS]: rsvp.guestsCount || DASHBOARD_CONSTANTS.DEFAULT_GUESTS,
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.ACCOMMODATION]: Array.isArray(rsvp.accommodationNeeds) 
        ? rsvp.accommodationNeeds.join(', ') 
        : (rsvp.needAccommodation || ''),
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.FAVORITE_SONG]: rsvp.favoriteSongChoice || rsvp.favoriteSong || '',
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.PHONE]: rsvp.phoneNumber || rsvp.contactInfo || '',
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.EMAIL]: rsvp.email || '',
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.ADDITIONAL]: rsvp.additionalInfo || '',
      [DASHBOARD_CONSTANTS.EXCEL.HEADERS.SUBMITTED]: rsvp.createdAt && rsvp.createdAt.toDate 
        ? rsvp.createdAt.toDate().toLocaleDateString() 
        : DASHBOARD_CONSTANTS.UNKNOWN_DATE
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...excelData.map(row => String(row[key as keyof typeof row] || '').length)
      )
    }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, DASHBOARD_CONSTANTS.EXCEL.SHEET_NAME);

    const date = new Date().toISOString().split('T')[0];
    const filename = `${DASHBOARD_CONSTANTS.EXCEL.FILENAME_PREFIX}${date}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  if (loading) {
    return (
      <div className={classes.dashboard}>
        <div className={classes.loadingContainer}>
          <div className={classes.loadingCard}>
            <div style={{ 
              fontSize: '3rem', 
              marginBottom: '1rem',
              animation: 'spin 2s linear infinite'
            }}>
              {DASHBOARD_CONSTANTS.LOADING.EMOJI}
            </div>
            <h2 style={{ 
              color: 'var(--primary-color)', 
              fontSize: '1.5rem', 
              marginBottom: '0.5rem',
              fontWeight: 600
            }}>
              {DASHBOARD_CONSTANTS.LOADING.TITLE}
            </h2>
            <p style={{ 
              color: '#6d4c41', 
              fontSize: '1rem',
              opacity: 0.8
            }}>
              {DASHBOARD_CONSTANTS.LOADING.DESCRIPTION}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalGuests = rsvps.reduce((sum, r) => sum + (r.guestsCount || DASHBOARD_CONSTANTS.DEFAULT_GUESTS), DASHBOARD_CONSTANTS.DEFAULT_GUESTS);
  
  const puneAccommodation = rsvps
    .filter(r => {
      if (Array.isArray(r.accommodationNeeds)) {
        return r.accommodationNeeds.includes('pune');
      }
      // For backward compatibility with old yes/no format
      return r.needAccommodation === 'yes';
    })
    .reduce((sum, r) => sum + 1 + (r.guestsCount || DASHBOARD_CONSTANTS.DEFAULT_GUESTS), 0);

  const mumbaiAccommodation = rsvps
    .filter(r => {
      if (Array.isArray(r.accommodationNeeds)) {
        return r.accommodationNeeds.includes('mumbai');
      }
      // For backward compatibility, we don't count old yes/no as Mumbai since we don't know the city
      return false;
    })
    .reduce((sum, r) => sum + 1 + (r.guestsCount || DASHBOARD_CONSTANTS.DEFAULT_GUESTS), 0);

  const filteredRsvps = rsvps.filter(rsvp => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const name = rsvp.name || '';
    return name.toLowerCase().includes(searchLower);
  });

  if (rsvps.length === 0) {
    return (
      <div className={classes.dashboard}>
        <div className={classes.topBar}>
          <button onClick={handleLogout} className={classes.logoutButton}>
            <SignOut24Regular style={{ fontSize: '16px' }} />
            {DASHBOARD_CONSTANTS.LOGOUT_BUTTON}
          </button>
        </div>

        <div className={classes.title}>
          <h1 className={classes.titleText}>{DASHBOARD_CONSTANTS.TITLE}</h1>
        </div>

        <div className={classes.noRsvpsContainer}>
          <div className={classes.noRsvpsCard}>
            <div className={classes.emoji}>{DASHBOARD_CONSTANTS.NO_RSVPS.EMOJI}</div>
            <h2 className={classes.noRsvpsTitle}>{DASHBOARD_CONSTANTS.NO_RSVPS.TITLE}</h2>
            <p className={classes.noRsvpsText}>
              {DASHBOARD_CONSTANTS.NO_RSVPS.MESSAGE.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < DASHBOARD_CONSTANTS.NO_RSVPS.MESSAGE.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <div className={classes.emojiRow}>
              {DASHBOARD_CONSTANTS.NO_RSVPS.EMOJI_ROW}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.dashboard}>
      <div className={classes.topBar}>
        <button onClick={handleLogout} className={classes.logoutButton}>
          <SignOut24Regular style={{ fontSize: '16px' }} />
          {DASHBOARD_CONSTANTS.LOGOUT_BUTTON}
        </button>
      </div>

      <div className={classes.title}>
        <h1 className={classes.titleText}>{DASHBOARD_CONSTANTS.TITLE}</h1>
      </div>

      <section className={classes.summarySection}>
        <div className={classes.summaryTitle}>{DASHBOARD_CONSTANTS.SUMMARY.TITLE}</div>
        <div className={classes.statsGrid}>
          <div>
            <strong style={{ color: 'var(--primary-color)' }}>{DASHBOARD_CONSTANTS.SUMMARY.TOTAL_RSVPS}</strong>
            <br />
            <span className={classes.statsValue}>{rsvps.length}</span>
          </div>
          <div>
            <strong style={{ color: 'var(--primary-color)' }}>{DASHBOARD_CONSTANTS.SUMMARY.TOTAL_GUESTS}</strong>
            <br />
            <span className={classes.statsValue}>{totalGuests}</span>
          </div>
          <div>
            <strong style={{ color: 'var(--primary-color)' }}>{DASHBOARD_CONSTANTS.SUMMARY.PUNE_ACCOMMODATION}</strong>
            <br />
            <span className={classes.statsValue}>{puneAccommodation}</span>
          </div>
          <div>
            <strong style={{ color: 'var(--primary-color)' }}>{DASHBOARD_CONSTANTS.SUMMARY.MUMBAI_ACCOMMODATION}</strong>
            <br />
            <span className={classes.statsValue}>{mumbaiAccommodation}</span>
          </div>
        </div>
      </section>

      <section style={{ marginTop: '2rem' }}>
        <h3 style={{ color: 'var(--primary-color)', textAlign: 'center', marginBottom: '1rem' }}>{DASHBOARD_CONSTANTS.TABLE.TITLE}</h3>
        
        <div className={classes.tableContainer}>
          <div className={classes.searchContainer}>
            <label htmlFor="search" style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
              color: 'var(--primary-color)',
              fontSize: '1rem'
            }}>
              {DASHBOARD_CONSTANTS.TABLE.SEARCH_LABEL}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="search"
                type="text"
                placeholder={DASHBOARD_CONSTANTS.TABLE.SEARCH_PLACEHOLDER}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={classes.searchInput}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className={classes.clearButton}
                  title={DASHBOARD_CONSTANTS.TABLE.CLEAR_TITLE}
                >
                  {DASHBOARD_CONSTANTS.TABLE.CLEAR_SYMBOL}
                </button>
              )}
            </div>
            {searchTerm && (
              <div style={{
                marginTop: '0.5rem',
                fontSize: '0.9rem',
                color: 'var(--primary-color)',
                fontStyle: 'italic'
              }}>
                {DASHBOARD_CONSTANTS.TABLE.SEARCH_RESULTS_PREFIX} {filteredRsvps.length} of {rsvps.length} {DASHBOARD_CONSTANTS.TABLE.SEARCH_RESULTS_SUFFIX}
              </div>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            {filteredRsvps.length === 0 && searchTerm ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--primary-color)',
                fontSize: '1.1rem'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{DASHBOARD_CONSTANTS.TABLE.NO_RESULTS.EMOJI}</div>
                {DASHBOARD_CONSTANTS.TABLE.NO_RESULTS.MESSAGE_PREFIX}"{searchTerm}"{DASHBOARD_CONSTANTS.TABLE.NO_RESULTS.MESSAGE_SUFFIX}
                <br />
                <small style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                  {DASHBOARD_CONSTANTS.TABLE.NO_RESULTS.TIP}
                </small>
              </div>
            ) : (
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.NAME}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.EVENTS}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.GUESTS}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.ACCOMMODATION}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.FAVORITE_SONG}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.PHONE}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.EMAIL}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.ADDITIONAL}</th>
                    <th className={classes.tableHeader}>{DASHBOARD_CONSTANTS.HEADERS.SUBMITTED}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRsvps.map((rsvp, index) => (
                    <tr key={rsvp.id} style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(12, 59, 94, 0.05)',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td className={`${classes.tableCell} ${classes.nameCell}`}>{rsvp.name}</td>
                      <td className={classes.tableCell}>
                        {Array.isArray(rsvp.events) 
                          ? rsvp.events.join(', ') 
                          : rsvp.events || DASHBOARD_CONSTANTS.DEFAULT_VALUE
                        }
                      </td>
                      <td className={classes.tableCell}>{rsvp.guestsCount || DASHBOARD_CONSTANTS.DEFAULT_GUESTS}</td>
                      <td className={classes.tableCell}>
                        {Array.isArray(rsvp.accommodationNeeds) 
                          ? rsvp.accommodationNeeds.join(', ') 
                          : (rsvp.needAccommodation || DASHBOARD_CONSTANTS.DEFAULT_VALUE)}
                      </td>
                      { <td className={classes.tableCell}>{rsvp.favoriteSongChoice || rsvp.favoriteSong || DASHBOARD_CONSTANTS.DEFAULT_VALUE}</td> }
                      <td className={classes.tableCell}>{rsvp.phoneNumber || rsvp.contactInfo || DASHBOARD_CONSTANTS.DEFAULT_VALUE}</td>
                      <td className={classes.tableCell}>{rsvp.email || DASHBOARD_CONSTANTS.DEFAULT_VALUE}</td>
                      <td className={classes.tableCell}>{rsvp.additionalInfo || DASHBOARD_CONSTANTS.DEFAULT_VALUE}</td>
                      <td className={classes.tableCell}>
                        {rsvp.createdAt && rsvp.createdAt.toDate 
                          ? rsvp.createdAt.toDate().toLocaleDateString() 
                          : DASHBOARD_CONSTANTS.UNKNOWN_DATE
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      <section className={classes.exportSection}>
        <button
          onClick={exportToExcel}
          disabled={rsvps.length === 0}
          className={classes.exportButton}
        >
          {DASHBOARD_CONSTANTS.EXPORT_BUTTON}
        </button>
      </section>
    </div>
  );
};

export default Dashboard;